

















import { Component, Provide, Vue } from 'vue-property-decorator'
import { CompanyAPI, GamificationBadgeAPI, GamificationScoreAPI, GamificationSettingsAPI } from '@/api/backend-client'
import { User } from '@/api/model/user'
import { Company } from '@/api/model/company'
import { GamificationBadge } from '@/api/model/gamification-badge'
import { GamificationScore } from '@/api/model/gamification-score'

@Component
export default class Leaderboard extends Vue {
    loading: boolean = false

    async mounted() {
        await this.reloadCompany()
    }

    @Provide()
    async reloadCompany(): Promise<Company> {
        this.loading = true
        this.$currentCompany = await CompanyAPI.get(this.$currentCompany.id, true)
        await this.reloadCompanySettings()
        await this.reloadCompanyScoresAndBadges()
        this.loading = false

        return this.$currentCompany
    }

    async reloadCompanyScoresAndBadges(): Promise<void> {
        const [gamificationScores, gamificationBadges] = await Promise.all([
            this.loadGamificationScoresPerUser(),
            this.loadGamificationBadgesPerUser(),
        ])

        for (const user of this.$currentCompany.users) {
            if (!user || !user.id) {
                continue
            }

            if (user.resourceIdentifier in gamificationScores) {
                user.gamificationScores = gamificationScores[user.resourceIdentifier]
            }

            if (user.resourceIdentifier in gamificationBadges) {
                user.gamificationBadges = gamificationBadges[user.resourceIdentifier]
            }

            if (user.id === this.$currentUser.id) {
                this.$currentUser.gamificationScores = user.gamificationScores
                this.$currentUser.gamificationBadges = user.gamificationBadges
            }
        }
    }

    async loadGamificationScoresPerUser() {
        // Get gamification scores
        const gamificationScores = await GamificationScoreAPI.getAll({
            'user.company': this.$currentCompany.id,
        })

        // Get gamification scores indexed per user
        const gamificationScoresPerUser = {}

        gamificationScores.forEach((gamificationScore: GamificationScore) => {
            const user = gamificationScore.user instanceof User ? gamificationScore.user.id : gamificationScore.user as string
            if (!(user in gamificationScoresPerUser)) {
                gamificationScoresPerUser[user] = []
            }

            gamificationScoresPerUser[user].push(gamificationScore)
        })

        return gamificationScoresPerUser
    }

    async loadGamificationBadgesPerUser() {
        // Get gamification badges
        const gamificationBadges = await GamificationBadgeAPI.getAll({
            'user.company': this.$currentCompany.id,
        })

        // Get gamification badges indexed per user
        const gamificationBadgesPerUser = {}

        gamificationBadges.forEach((gamificationBadge: GamificationBadge) => {
            if (!this.isBadgeVisible(gamificationBadge)) {
                return
            }

            const user = gamificationBadge.user instanceof User ? gamificationBadge.user.id : gamificationBadge.user as string
            if (!(user in gamificationBadgesPerUser)) {
                gamificationBadgesPerUser[user] = []
            }

            gamificationBadgesPerUser[user].push(gamificationBadge)
        })

        return gamificationBadgesPerUser
    }

    isBadgeVisible(badge: GamificationBadge): boolean {
        const date = new Date()

        let lastQuarterYear = date.getFullYear()
        let lastQuarter = Math.floor(date.getMonth() - 1 / 3) // no need to subtract 1 because calculated quarter is 0-based

        if (lastQuarter === 0) {
            lastQuarterYear--
            lastQuarter = 4
        }

        // All quarters in current year until the last quarter
        if (badge.year === lastQuarterYear && badge.quarter <= lastQuarter) {
            return true
        }

        // All quarters in previous year after the last quarter
        if (badge.year === lastQuarterYear - 1 && badge.quarter > lastQuarter) {
            return true
        }

        return false
    }

    async reloadCompanySettings(): Promise<void> {
        this.$currentCompany.gamificationSettings = await GamificationSettingsAPI.get(
            this.$currentCompany.id,
        )
    }
}
